import { useGetContractedCompanies } from "@/entities/get-contracted-companies/fetch";
import { useGetOffersCargo } from "@/entities/get-offers-cargo/fetch";
import { useMultipleGetVehicle } from "@/entities/get-vehicle/fetch";
import { useGetVehicles } from "@/entities/get-vehicles/fetch";
import { usePostOffersCargosReject } from "@/entities/post-offers-cargos-reject/fetch";
import { usePutOffersCargo } from "@/entities/put-offers-cargo/fetch";
import { useGlobalNotification } from "@/entities/set-global-notification/fetch";
import { useInProgressValuesLocalCache } from "@/features/offer-cargo-create-in-progress-values-local-cache";
import { fetchOfferCargoProcess } from "@/features/offer-cargo-process/fetch";
import { useAuth } from "@/shared/auth/fetch/useAuth";
import { excludeNull } from "@/shared/lib/collection";
import { fetchFileFromUrl } from "@/shared/lib/file";
import { isSuccessStatusCode } from "@/shared/lib/http";
import { type Loadable, transformLoadable } from "@/shared/lib/loadable";
import { safeParseInt } from "@/shared/lib/number";
import { loadableFromUseQueryResult } from "@/shared/lib/tanstack-query";
import { Result } from "neverthrow";
import { useCallback, useMemo, useState } from "react";
import type {
  AdminDeliveryProposalDetailPageProps,
  DeliveryProposalOfferCargoDestination,
  MemberVehicle,
} from "../model";

export const useAdminDeliveryProposalDetailPageProps = (
  offerCargoIdStr: string | undefined,
): AdminDeliveryProposalDetailPageProps => {
  // Auth
  const { session } = useAuth();

  // State
  const [previewValues, setPreviewValues] =
    useState<AdminDeliveryProposalDetailPageProps["previewValues"]>();

  // Local Storage
  // 記入途中の情報をローカルストレージに保存する
  const [inProgressValues, setInProgressValues, removeInProgressValues] =
    useInProgressValuesLocalCache<
      AdminDeliveryProposalDetailPageProps["defaultInputValues"]
    >("AdminDeliveryProposalDetailPage");

  // Backend API
  const offerCargoId = useMemo(() => {
    if (!offerCargoIdStr) {
      return undefined;
    }
    const id = safeParseInt(offerCargoIdStr);
    if (!id) {
      return undefined;
    }
    return id;
  }, [offerCargoIdStr]);
  const getDeliveryProposalOfferCargo = useGetOffersCargo(
    {
      id: offerCargoId ?? -1,
    },
    {
      enabled: !!offerCargoId,
    },
  );

  // 企業一覧取得
  const getCompaniesQuery = useGetContractedCompanies(
    {
      pagination: { displayLimit: 10000 },
    },
    { enabled: true },
  );

  // 契約データ取得
  const processId = useMemo(() => {
    return getDeliveryProposalOfferCargo.data?.data?.process?.id;
  }, [getDeliveryProposalOfferCargo.data]);

  const { offerCargoProcess } = fetchOfferCargoProcess(processId);

  const putOffersCargo = usePutOffersCargo();
  const postOffersCargosReject = usePostOffersCargosReject();

  const getVehicles = useGetVehicles(
    {
      search: {
        companyId: session.isAuthenticated ? session.user.companyId : undefined,
      },
      pagination: {
        displayLimit: 10000,
      },
    },
    {
      enabled: session.isAuthenticated,
    },
  );
  // workaround: getVehiclesにはequipmentsが入っていないので単品APIで取り直す
  const getMultipleVehicles = useMultipleGetVehicle(
    Object.fromEntries(
      getVehicles.data?.data.vehicles.map((vehicle) => [
        vehicle.id,
        { id: vehicle.id },
      ]) ?? [],
    ),
    {
      enabled: !!getVehicles.data?.data.vehicles,
    },
  );

  // Global Notification
  const { setGlobalNotification } = useGlobalNotification();

  // Props
  const deliveryProposalOfferCargo = useMemo<
    AdminDeliveryProposalDetailPageProps["deliveryProposalOfferCargo"]
  >(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getDeliveryProposalOfferCargo),
      (offerCargo) => {
        return offerCargo.data;
      },
    );
  }, [getDeliveryProposalOfferCargo]);

  const draftOfferCargo = useMemo<
    AdminDeliveryProposalDetailPageProps["draftOfferCargo"]
  >(() => {
    return transformLoadable(offerCargoProcess, (process) => {
      return process.draftOfferCargo;
    });
  }, [offerCargoProcess]);

  // 企業一覧の変換
  const companies = useMemo(() => {
    return transformLoadable(
      loadableFromUseQueryResult(getCompaniesQuery),
      (data) =>
        data.data.companies.filter((c) => c.category?.includes("carrier")),
    );
  }, [getCompaniesQuery]);

  // 契約データの変換
  const contractOfferCargos = useMemo<
    AdminDeliveryProposalDetailPageProps["contractOfferCargos"]
  >(() => {
    return transformLoadable(offerCargoProcess, (offerCargoProcess) => {
      return offerCargoProcess.contractOfferCargos;
    });
  }, [offerCargoProcess]);

  // Actions
  const onInputValuesUpdate = useCallback<
    AdminDeliveryProposalDetailPageProps["onInputValuesUpdate"]
  >(
    (deliveryProposalOfferCargo) => {
      setInProgressValues(deliveryProposalOfferCargo);
    },
    [setInProgressValues],
  );

  const onConfirmClick = useCallback<
    AdminDeliveryProposalDetailPageProps["onConfirmClick"]
  >(
    async (deliveryProposalOfferCargo) => {
      if (!deliveryProposalOfferCargo.vehicleId) {
        setPreviewValues({
          ...deliveryProposalOfferCargo,
          vehicle: {
            status: "success",
            data: undefined,
          },
        });
      } else {
        const getDeliveryProposalOfferCargoVehicle =
          deliveryProposalOfferCargo.vehicleId
            ? getMultipleVehicles[deliveryProposalOfferCargo.vehicleId]
            : null;
        if (!getDeliveryProposalOfferCargoVehicle) {
          console.error(
            `vehicle not found ${deliveryProposalOfferCargo.vehicleId}`,
          );
          return;
        }
        const vehicle = transformLoadable(
          loadableFromUseQueryResult(getDeliveryProposalOfferCargoVehicle),
          (vehicle) => vehicle.data,
        );
        setPreviewValues({
          ...deliveryProposalOfferCargo,
          vehicle,
        });
      }
    },
    [getMultipleVehicles],
  );

  const onBackToInProgressClick = useCallback<
    AdminDeliveryProposalDetailPageProps["onBackToInProgressClick"]
  >(() => {
    setPreviewValues(undefined);
  }, []);

  const onRejectClick = useCallback<
    AdminDeliveryProposalDetailPageProps["onRejectClick"]
  >(
    async (params) => {
      if (!offerCargoId) {
        setGlobalNotification({
          variant: "error",
          message: "配送提案のIDが不正です。",
        });
        return;
      }
      try {
        const response = await postOffersCargosReject.mutateAsync({
          id: offerCargoId.toString(),
          data: params,
        });
        if (response && isSuccessStatusCode(response.status)) {
          setGlobalNotification({
            variant: "success",
            message: "配送提案を見送りました",
            autoHide: true,
          });
          await getDeliveryProposalOfferCargo.refetch();
        } else {
          setGlobalNotification({
            variant: "error",
            message: "配送提案の見送りに失敗しました",
          });
        }
      } catch {
        setGlobalNotification({
          variant: "error",
          message: "配送提案の見送りに失敗しました",
        });
      }
    },
    [
      offerCargoId,
      postOffersCargosReject,
      setGlobalNotification,
      getDeliveryProposalOfferCargo,
    ],
  );

  const onSubmitClick = useCallback<
    AdminDeliveryProposalDetailPageProps["onSubmitClick"]
  >(async () => {
    if (!offerCargoId) {
      setGlobalNotification({
        variant: "error",
        message: "配送提案のIDが不正です。",
      });
      return;
    }
    if (!previewValues) {
      return;
    }
    const deliveryProposalOfferCargoData = deliveryProposalOfferCargo.data;
    if (!deliveryProposalOfferCargoData) {
      return;
    }
    const destinations = deliveryProposalOfferCargoData.destinations.map<
      (typeof deliveryProposalOfferCargoData.destinations)[0]
    >((destination, index) => {
      let proposalDestination:
        | DeliveryProposalOfferCargoDestination
        | undefined = undefined;
      if (index === 0) {
        proposalDestination = previewValues.loadingDestination;
      } else if (
        index ===
        deliveryProposalOfferCargoData.destinations.length - 1
      ) {
        proposalDestination = previewValues.unloadingDestination;
      } else {
        proposalDestination = previewValues.transitDestinations[index - 1];
      }
      if (!proposalDestination) {
        return destination;
      }
      return {
        ...destination,
        deadlineType:
          proposalDestination.deadlineType ?? destination.deadlineType,
        deadlineTime:
          proposalDestination.deadlineTime ?? destination.deadlineTime,
        deadlineTimeEnd:
          proposalDestination.deadlineTimeEnd ?? destination.deadlineTimeEnd,
        taskDetail: proposalDestination.taskDetail ?? destination.taskDetail,
        memo: proposalDestination.memo ?? destination.memo,
      };
    });
    const destinationFilesResults = await Promise.all(
      excludeNull(
        deliveryProposalOfferCargoData.attachedDestinationFiles?.map((file) => {
          if (!file.url || !file.filename) {
            return null;
          }
          return fetchFileFromUrl(file.url, file.filename);
        }) ?? [],
      ),
    );
    const destinationFiles = Result.combine(destinationFilesResults);
    if (destinationFiles.isErr()) {
      setGlobalNotification({
        variant: "error",
        message:
          "申し訳ありません。配送提案の更新に失敗しました。ページをリロードしてもう一度お試しください。",
      });
      return;
    }
    const deliveryRequestFilesResults = await Promise.all(
      excludeNull(
        deliveryProposalOfferCargoData.attachedDeliveryRequestFiles?.map(
          (file) => {
            if (!file.url || !file.filename) {
              return null;
            }
            return fetchFileFromUrl(file.url, file.filename);
          },
        ) ?? [],
      ),
    );
    const deliveryRequestFiles = Result.combine(deliveryRequestFilesResults);
    if (deliveryRequestFiles.isErr()) {
      setGlobalNotification({
        variant: "error",
        message:
          "申し訳ありません。配送提案の更新に失敗しました。ページをリロードしてもう一度お試しください。",
      });
      return;
    }
    const destinationsWithSplitAddress = destinations.map((destination) => {
      if (!destination.address)
        return { ...destination, city: "", addressLine: "" };

      const parts = destination.address.split(" ");
      const city = parts.length > 1 ? parts[0] : destination.address;
      const addressLine =
        parts.length > 1 ? parts.slice(1).join(" ") : undefined;

      return {
        ...destination,
        city,
        addressLine,
      };
    });

    const response = await putOffersCargo.mutateAsync({
      id: offerCargoId,
      data: {
        ...deliveryProposalOfferCargoData,
        ...previewValues,
        vehicleId: previewValues.vehicleId ?? null,
        destinations: destinationsWithSplitAddress,
      },
    });
    if (response && isSuccessStatusCode(response.status)) {
      setGlobalNotification({
        variant: "success",
        message: "配送提案を更新しました",
        autoHide: true,
      });
      removeInProgressValues();
      await getDeliveryProposalOfferCargo.refetch();
      setPreviewValues(undefined);
    } else {
      setGlobalNotification({
        variant: "error",
        message: "配送提案更新に失敗しました",
      });
    }
  }, [
    offerCargoId,
    deliveryProposalOfferCargo.data,
    previewValues,
    putOffersCargo,
    getDeliveryProposalOfferCargo,
    setGlobalNotification,
    removeInProgressValues,
  ]);

  const vehicles = useMemo<Loadable<MemberVehicle[]>>(() => {
    const vehicleLoadbles = Object.values(getMultipleVehicles).map(
      (vehicleQuery) => {
        return transformLoadable(
          loadableFromUseQueryResult(vehicleQuery),
          (vehicle) => vehicle.data,
        );
      },
    );
    if (vehicleLoadbles.some((vehicle) => vehicle.status === "error")) {
      return {
        status: "error",
        error: {
          message: vehicleLoadbles
            .filter((vehicle) => vehicle.status === "error")
            .map((vehicle) => vehicle.error?.message)
            .join(", "),
        },
      };
    }
    if (vehicleLoadbles.some((vehicle) => vehicle.status === "loading")) {
      return {
        status: "loading",
        data: undefined,
      };
    }
    return {
      status: "success",
      data: excludeNull(vehicleLoadbles.map((vehicle) => vehicle.data)),
    };
  }, [getMultipleVehicles]);
  return {
    deliveryProposalOfferCargo,
    draftOfferCargo,
    companies,
    contractOfferCargos,
    defaultInputValues: inProgressValues,
    vehicles,
    previewValues,
    deliveryProposalId: offerCargoId,
    onInputValuesUpdate,
    onBackToInProgressClick,
    onSubmitClick,
    onConfirmClick,
    onRejectClick,
  };
};
